<script>
export const reuseComponent = true
</script>

<script setup>
import { computed } from 'vue'
import StatusItem from './LiveLocationWidget/components/StatusItem.vue'
import BatteryInfoItem from './LiveLocationWidget/components/BatteryInfoItem.vue'
import icons, { LocationIcon, PersonIcon, PhoneIcon } from './LiveLocationWidget/icons'

const props = defineProps({
  item: Object,
})

const UNKNOWN_TEXT = 'Onbekend'

const formatSpeed = (speed) => {
  if (speed === undefined || speed === null) return UNKNOWN_TEXT
  return `${Math.round(speed * 3.6)} km/h`
}

const formatDistance = (distance) => {
  if (distance === undefined || distance === null) return UNKNOWN_TEXT
  return `${distance} km`
}

const formatDate = (dateString) => {
  if (!dateString) return UNKNOWN_TEXT
  try {
    const date = new Date(dateString)
    return date.toLocaleString('nl-NL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch (e) {
    return dateString
  }
}

const locations = computed(() => {
  const enrichedLocations = Object.fromEntries((props.item?.enrichment?.locations || []).map((location) => [location.uuid, location]))

  return (props.item?.properties?.darioLiveLocations ?? [])?.map((location) => {
    const dario = enrichedLocations[location.dario?.uuid]
    const trackers = props.item?.enrichment?.locationTrackers?.[location.dario?.uuid] || []

    return {
      ...location,
      dario,
      trackers,
    }
  })
})

const distance = computed(() => {
  return props.item?.enrichment?.distance
})
</script>

<template>
  <div
    class="absolute rounded-lg bg-gray-800 text-white overflow-hidden shadow-lg border border-gray-700 space-y-2"
    :style="{
      left: `${item.properties.position.xLeft}px`,
      top: `${item.properties.position.yTop}px`,
      width: `${item.properties.position.width}px`,
      height: `${item.properties.position.height}px`,
      '--base-font-size': `${item.properties.position.fontSize || 16}px`,
    }"
    :class="`text-[length:var(--base-font-size)]`"
  >
    <div class="p-2 space-y-2">
      <h2 class="font-bold flex items-center justify-between flex-wrap">
        <div class="flex items-center">
          <LocationIcon class="w-[calc(var(--base-font-size)*1.25)] h-[calc(var(--base-font-size)*1.25)] mr-1 text-blue-400" />
          Live Locations
        </div>
        <div class="text-blue-300 font-normal text-[calc(var(--base-font-size)*0.75)]">
          <span class="bg-blue-500 bg-opacity-20 px-1.5 py-0.5 rounded-md"> {{ locations.length }} locatie{{ locations.length !== 1 ? 's' : '' }} </span>
        </div>
      </h2>
      <div v-if="distance" class="mt-2 text-[calc(var(--base-font-size)*0.875)] bg-blue-500 bg-opacity-10 rounded-md p-2 border border-blue-500 border-opacity-20">
        <div class="flex items-center justify-between">
          <span class="font-medium">Afstand tussen locaties:</span>
          <span class="text-blue-300 font-bold">{{ formatDistance(distance) }}</span>
        </div>
      </div>
    </div>

    <div v-for="location in locations" :key="location.uuid" class="bg-gray-700 p-2 transition-colors shadow-md border border-gray-600 transform space-y-4">
      <div class="flex flex-wrap justify-between items-center">
        <h3 class="font-semibold text-blue-300 flex items-center text-[calc(var(--base-font-size)*0.875)]">
          <PersonIcon class="w-[calc(var(--base-font-size)*1.25)] h-[calc(var(--base-font-size)*1.25)] mr-1 flex-shrink-0" />
          <span class="truncate">{{ location.dario.name }}</span>
        </h3>
        <div class="flex items-center">
          <span class="text-blue-300 bg-blue-500 bg-opacity-20 px-1.5 py-0.5 rounded-md text-[calc(var(--base-font-size)*0.75)]">
            {{ (location.trackers || []).length }} tracker{{ (location.trackers || []).length !== 1 ? 's' : '' }}
          </span>
        </div>
      </div>

      <div v-for="tracker in location.trackers" :key="tracker.uuid" class="pt-2 border-t border-gray-600 space-y-4">
        <div class="flex justify-between items-center">
          <span class="font-medium flex items-center text-[calc(var(--base-font-size)*0.75)] flex-row justify-between w-full">
            <span class="flex items-center">
              <PhoneIcon class="w-[calc(var(--base-font-size)*1)] h-[calc(var(--base-font-size)*1)] mr-1" />
              {{ tracker.name }}</span
            >
          </span>
        </div>

        <div class="grid grid-cols-2 gap-3 text-[calc(var(--base-font-size)*0.75)]">
          <StatusItem :icon="icons.time" :value="formatDate(tracker?.most_recent_location?.recorded_at)" />

          <StatusItem :icon="icons.speed" :value="formatSpeed(tracker?.most_recent_location?.speed)" />

          <BatteryInfoItem :level="tracker?.most_recent_location?.battery_level" :status="tracker?.most_recent_location?.battery_status" class="col-span-2" />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
:deep(.status-item-label) {
  font-size: calc(var(--base-font-size) * 0.65);
}

:deep(.status-item-value) {
  font-size: calc(var(--base-font-size) * 0.75);
}

:deep(.status-item-icon) {
  width: calc(var(--base-font-size) * 1);
  height: calc(var(--base-font-size) * 1);
}

:deep(.status-item-icon svg) {
  width: calc(var(--base-font-size) * 0.66);
  height: calc(var(--base-font-size) * 0.66);
}
</style>
